import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import {
  getFacebookConversationsWithMeta,
  getConversationTagsBatch,
} from "@api/services/facebook";
import { getZaloConversationsWithMeta } from "@api/services/zalo";
import { CHANNELS, PLATFORMS } from "@common";
import type { Conversation } from "@interfaces/chat.interface";
import { ConversationTabHelpers, type ConversationTabType } from "@constants/conversationTabs";

type Pagination = {
  offset: number;
  total: number;
  limit: number;
  hasMore: boolean;
};

type Page = { id: string; name: string };
type OA = { id: string; name: string };

type Params = {
  selectedFanpageName?: string;
  selectedChannel: string;
  debouncedSearchValue: string;
  facebookPages: Page[];
  zaloOAs: OA[];
  conversationPagination: Record<string, Pagination>;
  appendConversations: (conversations: Conversation[], pageId: string, pagination: Pagination) => void;
  activeTab: ConversationTabType;
  updateConversationTagsMap: (tagsMap: Record<string, Array<{ description: string; id: number; tagName: string }>>) => void;
  displayConversationsLength: number;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
};

export const useConversationLoadMore = ({
  selectedFanpageName,
  selectedChannel,
  debouncedSearchValue,
  facebookPages,
  zaloOAs,
  conversationPagination,
  appendConversations,
  activeTab,
  updateConversationTagsMap,
  displayConversationsLength,
  scrollContainerRef,
}: Params) => {
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const isLoadingMoreRef = useRef(false);
  const handleLoadMoreRef = useRef<(() => Promise<void>) | undefined>(undefined);

  const handleLoadMore = useCallback(async () => {
    if (
      !selectedFanpageName ||
      isLoadingMoreRef.current ||
      debouncedSearchValue
    ) {
      return;
    }

    if (selectedChannel === CHANNELS.ZALO) {
      const selectedOA = zaloOAs.find((oa) => oa.name === selectedFanpageName);
      if (!selectedOA) {
        return;
      }

      const pagination = conversationPagination[selectedOA.id];
      if (!pagination || !pagination.hasMore) {
        return;
      }
      isLoadingMoreRef.current = true;
      setIsLoadingMore(true);

      try {
        const nextOffset = pagination.offset + pagination.limit;

        const result = await getZaloConversationsWithMeta({
          oaId: selectedOA.id,
          offset: nextOffset,
          limit: pagination.limit,
        });

        const conversationsWithFanpage = result.conversations.map((conv) => ({
          ...conv,
          fanpageName: selectedFanpageName,
        }));

        const currentLoaded = result.offset + result.conversations.length;
        const hasMoreData =
          currentLoaded < result.total && result.conversations.length > 0;

        appendConversations(conversationsWithFanpage, selectedOA.id, {
          offset: result.offset,
          total: result.total,
          limit: result.limit,
          hasMore: hasMoreData,
        });
      } catch (error) {
        console.error("Error loading more Zalo conversations:", error);
      } finally {
        isLoadingMoreRef.current = false;
        setIsLoadingMore(false);
      }
      return;
    }

    const selectedPage = facebookPages.find(
      (page) => page.name === selectedFanpageName
    );

    if (!selectedPage) {
      return;
    }

    const pagination = conversationPagination[selectedPage.id];
    if (!pagination || !pagination.hasMore) {
      return;
    }
    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);

    try {
      const nextOffset = pagination.offset + pagination.limit;

      let folder: string | undefined;
      let excludeFolders: string[] | undefined;

      if (!ConversationTabHelpers.requiresFolderStatus(activeTab)) {
        excludeFolders = [];
      } else {
        folder = ConversationTabHelpers.getFolderForTab(activeTab);
      }

      const result = await getFacebookConversationsWithMeta({
        pageId: selectedPage.id,
        offset: nextOffset,
        limit: pagination.limit,
        folder,
        excludeFolders,
      });

      const conversationsWithFanpage = result.conversations.map((conv) => ({
        ...conv,
        fanpageName: selectedFanpageName,
      }));

      const currentLoaded = result.offset + result.conversations.length;
      const hasMoreData =
        currentLoaded < result.total && result.conversations.length > 0;

      appendConversations(conversationsWithFanpage, selectedPage.id, {
        offset: result.offset,
        total: result.total,
        limit: result.limit,
        hasMore: hasMoreData,
      });

      const externalIds = conversationsWithFanpage
        .filter((conv) => conv.channel === CHANNELS.FACEBOOK)
        .map((conv) => conv.lastMessage?.conversationId)
        .filter((id): id is string => Boolean(id));

      if (externalIds.length > 0) {
        getConversationTagsBatch({
          externalIds: externalIds,
          platform: PLATFORMS.FACEBOOK,
          pageId: selectedPage.id,
        })
          .then((tagsMap) => {
            updateConversationTagsMap(tagsMap);
          })
          .catch((error) => {
            console.error("Error fetching conversation tags batch:", error);
          });
      }
    } catch (error) {
      console.error("Error loading more conversations:", error);
    } finally {
      isLoadingMoreRef.current = false;
      setIsLoadingMore(false);
    }
  }, [
    selectedFanpageName,
    selectedChannel,
    debouncedSearchValue,
    facebookPages,
    zaloOAs,
    conversationPagination,
    appendConversations,
    activeTab,
    updateConversationTagsMap,
  ]);

  useEffect(() => {
    handleLoadMoreRef.current = handleLoadMore;
  }, [handleLoadMore]);

  const currentPagination = useMemo(() => {
    if (!selectedFanpageName) return null;

    if (selectedChannel === CHANNELS.ZALO) {
      const selectedOA = zaloOAs.find((oa) => oa.name === selectedFanpageName);
      if (!selectedOA) return null;
      return conversationPagination[selectedOA.id] || null;
    }

    const selectedPage = facebookPages.find(
      (page) => page.name === selectedFanpageName
    );
    if (!selectedPage) return null;
    return conversationPagination[selectedPage.id] || null;
  }, [selectedFanpageName, selectedChannel, facebookPages, zaloOAs, conversationPagination]);

  const hasMore = currentPagination?.hasMore ?? false;

  const shouldSkipObserver = Boolean(
    debouncedSearchValue ||
      !hasMore ||
      isLoadingMore ||
      displayConversationsLength === 0
  );

  const { ref: loadMoreSentinelRef, inView } = useInView({
    root: scrollContainerRef.current,
    rootMargin: "100px",
    threshold: 0,
    skip: shouldSkipObserver,
    triggerOnce: false,
  });

  const prevInViewRef = useRef(false);
  const lastLoadMoreTimeRef = useRef<number>(0);
  const MIN_LOAD_MORE_INTERVAL = 500;

  useEffect(() => {
    const now = Date.now();
    const timeSinceLastLoad = now - lastLoadMoreTimeRef.current;

    const inViewChangedToTrue = !prevInViewRef.current && inView;

    if (
      inViewChangedToTrue &&
      hasMore &&
      !isLoadingMore &&
      !debouncedSearchValue &&
      !isLoadingMoreRef.current &&
      timeSinceLastLoad >= MIN_LOAD_MORE_INTERVAL &&
      handleLoadMoreRef.current
    ) {
      lastLoadMoreTimeRef.current = now;
      handleLoadMoreRef.current();
    }

    prevInViewRef.current = inView;
  }, [inView, hasMore, isLoadingMore, debouncedSearchValue]);

  return { isLoadingMore, hasMore, loadMoreSentinelRef };
};
